import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { StatusChip } from '../../shared/components/StatusChip';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { EntityType, EntityTypeSkill, EntityTypeSkillCadence, EntityTypeSkillScope, EntityTypeSkillStatus } from '../../core/types/data-models';

const CADENCE_LABELS: Record<EntityTypeSkillCadence, string> = {
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ON_DEMAND: 'On demand',
};

const SCOPE_LABELS: Record<EntityTypeSkillScope, string> = {
  ENTITY_TYPE: 'Entity Type',
  ENTITY: 'Entity',
};

const SCOPE_FILTER_ALL = 'ALL' as const;
type ScopeFilter = EntityTypeSkillScope | typeof SCOPE_FILTER_ALL;

// ─── Skill dialog ─────────────────────────────────────────────────────────────

interface SkillDialogProps {
  open: boolean;
  initial?: EntityTypeSkill | null;
  entityType: string;
  onClose: () => void;
}

function SkillDialog({ open, initial, entityType, onClose }: SkillDialogProps) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [contentMode, setContentMode] = useState<'markdown' | 'preview'>('markdown');
  const [order, setOrder] = useState('0');
  const [status, setStatus] = useState<EntityTypeSkillStatus>('ACTIVE');
  const [cadence, setCadence] = useState<EntityTypeSkillCadence>('ON_DEMAND');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setDescription(initial?.description ?? '');
      setContent(initial?.content ?? '');
      setContentMode('markdown');
      setOrder(initial?.order != null ? String(initial.order) : '0');
      setStatus(initial?.status ?? 'ACTIVE');
      setCadence(initial?.cadence ?? 'ON_DEMAND');
      setError(null);
    }
  }, [open, initial]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        entityType,
        name: name.trim(),
        description: description.trim(),
        content: content.trim(),
        order: order !== '' ? Number(order) : null,
        status,
        cadence,
      };
      if (initial) return resources.entityTypeSkills.update(initial.id, payload);
      return resources.entityTypeSkills.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entityTypeSkills'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  return (
    <FormDialog
      open={open}
      title={initial ? 'Edit Skill' : 'New Skill'}
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
      loading={save.isPending}
      maxWidth="lg"
      submitLabel={initial ? 'Save' : 'Create'}
    >
      <Box>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <TextField label="Description" required value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={2} />
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Content
              </Typography>
              <ToggleButtonGroup
                value={contentMode}
                exclusive
                size="small"
                onChange={(_e, val) => { if (val) setContentMode(val); }}
              >
                <ToggleButton value="markdown">Markdown</ToggleButton>
                <ToggleButton value="preview">Preview</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {contentMode === 'markdown' ? (
              <TextField
                required
                fullWidth
                value={content}
                onChange={(e) => setContent(e.target.value)}
                multiline
                rows={14}
                helperText="Instructional skill fragment for this entity type."
              />
            ) : (
              <Box
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  minHeight: 340,
                  '& p': { mt: 0, mb: 1 },
                  '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
                  '& pre': { bgcolor: 'action.hover', p: 1, borderRadius: 1, overflowX: 'auto' },
                  '& code': { fontSize: 12, fontFamily: 'monospace' },
                }}
              >
                {content
                  ? <ReactMarkdown>{content}</ReactMarkdown>
                  : <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Nothing to preview.</Typography>
                }
              </Box>
            )}
          </Box>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Order"
              type="number"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              helperText="Display order within the entity type (lower = first)"
              inputProps={{ min: 0 }}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as EntityTypeSkillStatus)}
              sx={{ flex: 1 }}
            >
              {(['ACTIVE', 'DISABLED'] as const).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
            <TextField
              select
              label="Cadence"
              value={cadence}
              onChange={(e) => setCadence(e.target.value as EntityTypeSkillCadence)}
              helperText="Recommended re-run interval"
              sx={{ flex: 1 }}
            >
              {(['WEEKLY', 'MONTHLY', 'QUARTERLY', 'ON_DEMAND'] as const).map((v) => (
                <MenuItem key={v} value={v}>{CADENCE_LABELS[v]}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      </Box>
    </FormDialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function EntityTypeSkillsPage() {
  const qc = useQueryClient();

  const { data: entityTypesEnv, isLoading: loadingEntityTypes, error: entityTypesError } = useQuery({
    queryKey: ['entityTypes'],
    queryFn: () => resources.entityTypes.list({ page: 1, pageSize: 200, sort: 'name' }),
  });
  const entityTypes: EntityType[] = entityTypesEnv?.data ?? [];

  const [selectedEntityType, setSelectedEntityType] = useState('');

  // Fetched unfiltered so per-entity-type counts (shown in the dropdown) and
  // the selected type's list both come from one dataset.
  const { data: skillsEnv, isLoading: loadingSkills } = useQuery({
    queryKey: ['entityTypeSkills'],
    queryFn: () => resources.entityTypeSkills.list({ page: 1, pageSize: 1000, sort: 'order' }),
  });
  const allSkills: EntityTypeSkill[] = skillsEnv?.data ?? [];

  const skillCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of allSkills) counts.set(s.entityType, (counts.get(s.entityType) ?? 0) + 1);
    return counts;
  }, [allSkills]);

  // Entity types with skills pinned to the top (most skills first), rest alphabetical.
  const sortedEntityTypes = useMemo(() => {
    return entityTypes.slice().sort((a, b) => {
      const countA = skillCounts.get(a.name) ?? 0;
      const countB = skillCounts.get(b.name) ?? 0;
      if (countA !== countB) return countB - countA;
      return a.name.localeCompare(b.name);
    });
  }, [entityTypes, skillCounts]);

  useEffect(() => {
    if (!selectedEntityType && sortedEntityTypes.length > 0) {
      setSelectedEntityType(sortedEntityTypes[0].name);
    }
  }, [sortedEntityTypes, selectedEntityType]);

  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>(SCOPE_FILTER_ALL);

  const skills: EntityTypeSkill[] = allSkills
    .filter((s) => s.entityType === selectedEntityType && (scopeFilter === SCOPE_FILTER_ALL || s.scope === scopeFilter))
    .sort((a, b) => a.scope.localeCompare(b.scope) || a.name.localeCompare(b.name));

  const [skillDialog, setSkillDialog] = useState<{ open: boolean; editing: EntityTypeSkill | null }>({ open: false, editing: null });
  const [deleteTarget, setDeleteTarget] = useState<EntityTypeSkill | null>(null);

  const deleteSkill = useMutation({
    mutationFn: (s: EntityTypeSkill) => resources.entityTypeSkills.remove(s.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entityTypeSkills'] });
      setDeleteTarget(null);
    },
  });

  if (loadingEntityTypes) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (entityTypesError) {
    return <Alert severity="error">{extractErrorMessage(entityTypesError)}</Alert>;
  }

  return (
    <Box>
      <PageHeader
        title="Entity Type Skills"
        description="Define reusable instructional skills for each entity type."
      />

      {entityTypes.length === 0 ? (
        <Alert severity="info">No entity types yet. Create one under Entity types before adding skills.</Alert>
      ) : (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <TextField
              select
              label="Entity Type"
              value={selectedEntityType}
              onChange={(e) => setSelectedEntityType(e.target.value)}
              sx={{ minWidth: 280 }}
              SelectProps={{ renderValue: (value) => value as string }}
            >
              {sortedEntityTypes.map((et) => {
                const count = skillCounts.get(et.name) ?? 0;
                return (
                  <MenuItem key={et.id} value={et.name}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                      <span>{et.name}</span>
                      <Chip
                        label={count === 1 ? '1 skill' : `${count} skills`}
                        size="small"
                        variant={count > 0 ? 'filled' : 'outlined'}
                        color={count > 0 ? 'primary' : 'default'}
                        sx={{ ml: 2 }}
                      />
                    </Stack>
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              select
              label="Scope"
              value={scopeFilter}
              onChange={(e) => setScopeFilter(e.target.value as ScopeFilter)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value={SCOPE_FILTER_ALL}>All</MenuItem>
              {(['ENTITY_TYPE', 'ENTITY'] as const).map((v) => (
                <MenuItem key={v} value={v}>{SCOPE_LABELS[v]}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="h6" fontWeight={600}>Skills</Typography>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setSkillDialog({ open: true, editing: null })}>
              New Skill
            </Button>
          </Stack>
          {loadingSkills ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress size={24} /></Box>
          ) : skills.length === 0 ? (
            <Alert severity="info">No skills yet for this entity type. Click "New Skill" to add one.</Alert>
          ) : (
            <Box>
              {skills.map((s) => (
                <Box key={s.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1, px: 2, py: 1.25, '&:hover .row-actions': { opacity: 1 } }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Chip label={s.order ?? '—'} size="small" variant="outlined" sx={{ minWidth: 32, mt: 0.25 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                      {s.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{s.description}</Typography>
                      )}
                    </Box>
                    <Chip label={SCOPE_LABELS[s.scope]} size="small" variant="outlined" />
                    <Chip label={CADENCE_LABELS[s.cadence]} size="small" variant="outlined" />
                    <StatusChip value={s.status} />
                    <Stack direction="row" className="row-actions" sx={{ opacity: 0, transition: 'opacity 0.15s' }}>
                      <Tooltip title="Edit skill">
                        <IconButton size="small" onClick={() => setSkillDialog({ open: true, editing: s })}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete skill">
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(s)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Box>
          )}

          <SkillDialog
            open={skillDialog.open}
            initial={skillDialog.editing}
            entityType={selectedEntityType}
            onClose={() => setSkillDialog({ open: false, editing: null })}
          />
          <ConfirmDialog
            open={deleteTarget !== null}
            title="Delete Skill"
            message={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
            confirmLabel="Delete"
            destructive
            loading={deleteSkill.isPending}
            onConfirm={() => deleteTarget && deleteSkill.mutate(deleteTarget)}
            onClose={() => setDeleteTarget(null)}
          />
        </>
      )}
    </Box>
  );
}
